import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useOutletContext } from "react-router-dom";
import { database, auth } from "../firebaseConfig";
import { ref, onValue, limitToFirst, get, query as rtdbQuery } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { useActiveWorld } from "../hooks/useActiveWorld";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Timestamp,
  getCountFromServer,
} from "firebase/firestore";
import { firestore } from "../firebaseConfig";
import WorldDbTable from "./WorldDbTable";
import WorldTableTwo from "./WorldTableTwo";
import WorldSettings from "./WorldSettings";
import SmartInbox from "./SmartInbox";
import InboxView from "./InboxView/InboxView";
import WorldDbUploader from "./WorldDbUploader";
import WorldDbCreator from "./WorldDbCreator";
import EmailSentNotifier from "./EmailSentNotifier";
import OfferCreator from "./OfferCreator/OfferCreator"; 
import WorldDbMigrationModal from "./WorldDbMigrationModal";
import "./WorldDb.css";
import NewWorldDbTable from "./NewWorldDbTable";
import WorldDbEmptyStart from "./WorldDbEmptyStart";
import WorldEmptyBackdrop from "./WorldEmptyBackdrop";
import Leads from "./Leads";
import useDebounce from "../hooks/use-debounce";
import ApiPendingBanner from "./ApiPendingBanner";
import ApiStagingReview from "./ApiStagingReview";
import DataConnectorAlertModal from "./DataConnectorAlertModal";
import { DATA_CONNECTOR_SETTINGS_TAB } from "./dataConnectors";

// Einfacher Loading-Spinner
const LoadingSpinner = () => (
  <div className="simple-loading-overlay">
    <img src="/img/salesgod_dark.webp" alt="SalesGod Logo" className="simple-loading-spinner" />
  </div>
);

// Coming Soon Komponente für Payouts
const ComingSoon = () => (
  <div className="coming-soon-container">
    <div className="coming-soon-icon">
      <img src="/img/BonusClub_Logo.webp" alt="Coming Soon" />
    </div>
    <h2 className="coming-soon-title">Coming Soon</h2>
    <p className="coming-soon-text">This feature will be available soon.</p>
  </div>
);

const UploadButton = ({ onUpload, isLoading }) => {
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div className="upload-button-container">
      <label className={`upload-button ${isLoading ? 'loading' : ''}`}>
        {!isLoading && 'Upload Database'}
        <input
          type="file"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept=".csv,.xlsx,.xls"
          disabled={isLoading}
        />
      </label>
    </div>
  );
};

const WorldDb = () => {
  const { offerId } = useParams(); // URL-Parameter auslesen
  const navigate = useNavigate(); // Navigation-Hook
  const [searchParams] = useSearchParams();
  
  // activeView und setActiveView kommen jetzt vom WorldLayout via Outlet Context
  const { activeView, setActiveView } = useOutletContext() || {};
  
  // Fallback für activeView falls keine Context-Daten verfügbar (z.B. direkter Aufruf)
  const viewFromUrl = searchParams.get('view');
  const effectiveActiveView = activeView || (offerId ? "offerCreator" : (viewFromUrl || "tableOne"));

  // The new two-pane InboxView is now the default for everyone (own account,
  // team accounts, every browser/device). The `bc_inbox_v2` preference is kept
  // only as an explicit opt-out escape hatch: set it to 'false' to fall back to
  // the classic SmartInbox.
  const [useInboxV2] = useState(() => {
    try { return localStorage.getItem('bc_inbox_v2') !== 'false'; } catch (e) { return true; }
  });

  const isInboxView = effectiveActiveView === "smartInbox";
  const isLeadsView = effectiveActiveView === "leads";
  const isSettingsView = effectiveActiveView === "settings";
  const isOfferView = effectiveActiveView === "offerCreator";
  const isPayoutsView = effectiveActiveView === "payouts";
  const isTargetsView = !isInboxView && !isLeadsView && !isSettingsView && !isOfferView && !isPayoutsView;

  // Keep Targets, Inbox and Leads mounted after the first visit so switching
  // tabs does not remount (and flash white while data reloads).
  const [keepTargets, setKeepTargets] = useState(isTargetsView);
  const [keepInbox, setKeepInbox] = useState(isInboxView);
  const [keepLeads, setKeepLeads] = useState(isLeadsView);
  const [inboxEmpty, setInboxEmpty] = useState(null);
  const [leadsEmpty, setLeadsEmpty] = useState(null);
  const [emptyTheme, setEmptyTheme] = useState(null);

  useEffect(() => {
    if (isTargetsView) setKeepTargets(true);
    if (isInboxView) setKeepInbox(true);
    if (isLeadsView) setKeepLeads(true);
  }, [isTargetsView, isInboxView, isLeadsView]);

  // The inbox links here when Inbox Reply is configured but switched off, so it
  // has to land on that pane — the plain view switch would drop the user on the
  // cockpit and leave them hunting for the setting the hint just named.
  const openInboxReplySettings = useCallback(() => {
    navigate({ search: '?view=settings&tab=agents' });
  }, [navigate]);
  
  // Team-System: Hole die aktive World-ID (eigene oder Team-Owner's)
  const { userId: activeWorldUserId, isLoading: teamLoading, hasFeature, canWrite } = useActiveWorld();
  
  
  const [dbData, setDbData] = useState([]);
  const [fullDbData, setFullDbData] = useState([]); // Speichert alle Daten für Filterung
  const [filteredData, setFilteredData] = useState([]); // Alle gefilterten Daten
  const [displayedData, setDisplayedData] = useState([]); // Nur die angezeigten Daten (mit Limit)
  const [columns, setColumns] = useState([]);
  
  // Verwende die aktive World-ID (für Team-Mitglieder ist das die Owner's ID)
  const userId = activeWorldUserId;
  const [showUploader, setShowUploader] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [showApiReview, setShowApiReview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(100); // Anzahl der anzuzeigenden Einträge
  const [hasMoreData, setHasMoreData] = useState(false); // Gibt es weitere Daten zum Laden?
  const [currentFilter, setCurrentFilter] = useState(null); // Speichert den aktuellen Filter
  const [searchTerm, setSearchTerm] = useState(""); // Speichert den Suchbegriff
  const [statusFilterMode, setStatusFilterMode] = useState("Not Contacted"); // Speichert den Status-Filter
  const [importFilterMode, setImportFilterMode] = useState("all"); // Neuer State für Import-Filter
  const [sortField, setSortField] = useState(null); // Speichert das Sortierfeld
  const [sortDirection, setSortDirection] = useState(null); // Speichert die Sortierrichtung
  const [todayContactedCount, setTodayContactedCount] = useState(0); // Speichert die Anzahl der heute angeschriebenen Kontakte
  
  const [emailNotifier, setEmailNotifier] = useState({
    status: '', // 'success' oder 'error'
    message: '',
    scheduledTime: null,
    recipientEmail: null,
    senderEmail: null
  });

  const [needsMigration, setNeedsMigration] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);

  const [reloadFlag, setReloadFlag] = useState(0);

  const [lastVisible, setLastVisible] = useState(null);

  const [totalContactCount, setTotalContactCount] = useState(null);
  const [countReady, setCountReady] = useState(false);
  // Drop stale count replies (world switch / overlapping reloads) so a late
  // `0` from the previous world cannot flash the empty-start screen.
  const countRequestRef = useRef(0);

  // Debounce für effiziente Firestore-Queries
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const fetchTotalContactCount = useCallback(async () => {
    if (teamLoading || !userId) {
      setCountReady(false);
      return;
    }
    const requestId = ++countRequestRef.current;
    try {
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const snapshot = await getCountFromServer(dbCollectionRef);
      if (requestId !== countRequestRef.current) return;
      setTotalContactCount(snapshot.data().count);
      setCountReady(true);
    } catch (error) {
      if (requestId !== countRequestRef.current) return;
      console.error("Fehler beim Zählen der Kontakte:", error);
      setTotalContactCount(null);
      setCountReady(true);
    }
  }, [userId, teamLoading]);

  const reloadData = useCallback(() => {
    setReloadFlag(flag => flag + 1);
    fetchTotalContactCount();
  }, [fetchTotalContactCount]);

  useEffect(() => {
    const next = isTargetsView && countReady && totalContactCount === 0
      ? 'targets'
      : isInboxView && inboxEmpty === true
        ? 'inbox'
        : isLeadsView && leadsEmpty === true
          ? 'leads'
          : null;
    if (next) {
      setEmptyTheme(next);
      return;
    }
    const stillResolving = (isInboxView && inboxEmpty === null)
      || (isLeadsView && leadsEmpty === null)
      || (isTargetsView && !countReady);
    if (!stillResolving) setEmptyTheme(null);
  }, [
    isTargetsView, isInboxView, isLeadsView,
    countReady, totalContactCount, inboxEmpty, leadsEmpty,
  ]);

  // Aktualisierte buildFirestoreQuery Funktion für komplexere Filter
  const buildFirestoreQuery = (baseQuery, filters) => {
    let q = baseQuery;
    
    // Status-Filter
    if (filters.statusMode && filters.statusMode !== "all") {
      const status = filters.statusMode;
      
      if (status === "Not Contacted") {
        q = query(q, where("lead_contacted", "in", [null, "", "Not Contacted"]));
      } else {
        // Für alle anderen Status filtern wir nach dem exakten Wert.
        q = query(q, where("lead_contacted", "==", status));
      }
    }
    
    // Import-Filter
    if (filters.importMode && filters.importMode !== "all") {
      q = query(q, where("import_name", "==", filters.importMode));
    }
    
    // Such-Filter (nur für E-Mail und Business Name)
    if (filters.searchTerm && filters.searchTerm.trim() !== '') {
      const searchTerm = filters.searchTerm.toLowerCase();
      // Firestore kann nicht effizient auf mehreren Feldern gleichzeitig suchen.
      // Wir konzentrieren uns auf das wichtigste Feld: E-Mail.
      // Für eine Volltextsuche wären externe Tools wie Algolia oder Typesense nötig.
      q = query(q, 
        where("lead_email", ">=", searchTerm),
        where("lead_email", "<=", searchTerm + '\uf8ff')
      );
    }
    
    return q;
  };

  // Aktualisierter useEffect für Daten-Loading
  useEffect(() => {
    // Warte bis TeamContext fertig geladen hat
    if (teamLoading || !userId) {
      setDbData([]);
      setFullDbData([]);
      setFilteredData([]);
      setDisplayedData([]);
      setColumns([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    loadData();
  }, [
    userId, 
    reloadFlag, 
    sortField, 
    sortDirection, 
    displayLimit,
    statusFilterMode,
    importFilterMode,
    debouncedSearchTerm
  ]);

  // Aktualisierte loadData Funktion mit Filterung
  const loadData = async () => {
    // Warte bis TeamContext fertig geladen hat
    if (teamLoading || !userId) {
      setDbData([]);
      setFullDbData([]);
      setFilteredData([]);
      setDisplayedData([]);
      setColumns([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Basis-Query erstellen
      let q = collection(firestore, `World/${userId}/db`);
      
      // Filter anwenden
      q = buildFirestoreQuery(q, {
        statusMode: statusFilterMode,
        importMode: importFilterMode,
        searchTerm: debouncedSearchTerm
      });
      
      // Sortierung hinzufügen
      if (sortField) {
        q = query(q, orderBy(sortField, sortDirection));
      }
      
      // Limit für die erste Seite
      q = query(q, limit(displayLimit));
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const arrayData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        
        // Spalten bestimmen
        if (arrayData.length > 0) {
          const columnKeys = [...new Set(arrayData.flatMap(Object.keys))];
          const formattedColumns = columnKeys.map((key) => ({
            key,
            label: key.replace(/_/g, " ").toUpperCase(),
          }));
          setColumns(formattedColumns);
        }
        
        // States aktualisieren
        setFullDbData(arrayData);
        setFilteredData(arrayData);
        setDisplayedData(arrayData);
        setDbData(arrayData);
        
        // Pagination-Status setzen
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMoreData(snapshot.docs.length === displayLimit);
      } else {
        setFullDbData([]);
        setFilteredData([]);
        setDisplayedData([]);
        setDbData([]);
        setHasMoreData(false);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Fehler beim Laden der Firestore-Daten:", error);
      setLoading(false);
    }
  };

  // Aktualisierte loadMoreData Funktion mit Filterung
  const loadMoreData = async () => {
    if (!lastVisible || !hasMoreData || loadingMore) return;
  
    setLoadingMore(true);
    
    try {
      // Basis-Query erstellen
      let q = collection(firestore, `World/${userId}/db`);
      // Filter anwenden
      q = buildFirestoreQuery(q, {
        statusMode: statusFilterMode,
        importMode: importFilterMode,
        searchTerm: debouncedSearchTerm
      });
      // Sortierung hinzufügen
      if (sortField) {
        q = query(q, orderBy(sortField, sortDirection));
      }
      // Pagination mit startAfter
      q = query(q, startAfter(lastVisible), limit(100)); // Immer 100 pro Page
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const newData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setFullDbData(prev => [...prev, ...newData]);
        setFilteredData(prev => [...prev, ...newData]);
        setDisplayedData(prev => [...prev, ...newData]);
        setDbData(prev => [...prev, ...newData]);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMoreData(snapshot.docs.length === 100);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Fehler beim Laden weiterer Daten:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Berechne die Anzahl der heute kontaktierten Kontakte basierend auf allen gefilterten Daten
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Setze auf Beginn des Tages
    
    const contactedCount = filteredData.filter(row => {
      // Sicherstellen, dass row existiert, bevor wir auf Eigenschaften zugreifen
      if (!row) return false;
      
      // Sicherstellen, dass lead_last_email und timestamp existieren
      if (row.lead_last_email && row.lead_last_email.timestamp) {
        const emailTimestamp = parseInt(row.lead_last_email.timestamp);
        if (!isNaN(emailTimestamp)) {
          const emailDate = new Date(emailTimestamp);
          return emailDate >= today;
        }
      }
      return false;
    }).length;
    
    setTodayContactedCount(contactedCount);
  }, [filteredData]);

  // Auth-Überwachung wird jetzt vom TeamContext/useActiveWorld Hook übernommen
  // Die userId kommt direkt vom Hook (activeWorldUserId)

  // Prüfe, ob Migration benötigt wird
  useEffect(() => {
    // Warte bis TeamContext fertig geladen hat
    if (teamLoading || !userId) return;

    const checkMigrationStatus = async () => {
      try {
        // Es zählt nur, OB noch Legacy-Kontakte in der Realtime Database
        // liegen — nicht wie viele. Ohne limitToFirst lädt get() den gesamten
        // Alt-Knoten (bei großen Worlds zweistellige MB JSON) in den Browser,
        // nur um Object.keys().length zu bilden. Das lief bei jedem Öffnen der
        // WorldDb und blockierte dabei den Main Thread.
        const legacyProbe = rtdbQuery(
          ref(database, `/World/${userId}/db`),
          limitToFirst(1)
        );
        const snapshot = await get(legacyProbe);
        setNeedsMigration(snapshot.exists());
      } catch (error) {
        console.error("Fehler beim Prüfen des Migrationsstatus:", error);
      }
    };

    checkMigrationStatus();
  }, [userId, teamLoading]);

  // Handler für erfolgreiche Migration
  const handleMigrationComplete = () => {
    setNeedsMigration(false);
    setMigrationComplete(true);
    // Lade die Daten neu
    window.location.reload();
  };

  // Handler-Funktion für Sortierungsänderungen von den Tabellenkomponenten
  const handleSortChange = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Funktion zum Zurücksetzen des Notifiers
  const resetEmailNotifier = () => {
    setEmailNotifier({ status: '', message: '', scheduledTime: null, recipientEmail: null, senderEmail: null });
  };

  // Funktion, die von Kindkomponenten aufgerufen werden kann, um den Notifier zu aktualisieren
  // oder den Status direkt zu aktualisieren
  const handleEmailResult = (status, message, rowData, scheduledTime, recipientEmail, senderEmail) => {
    // Stelle sicher, dass scheduledTime ein Zeitstempel ist
    let validScheduledTime = scheduledTime;
    if (scheduledTime && typeof scheduledTime === 'string') {
      try {
        validScheduledTime = new Date(scheduledTime).getTime();
        if (isNaN(validScheduledTime)) {
          validScheduledTime = null;
        }
      } catch (e) {
        validScheduledTime = null;
      }
    }
    
    // Optimistic patch helper — applies the same field update across all three data arrays
    const patchContact = (msg) => {
      const isCritical = msg._critical_update === true;
      const updater = (prevData) => {
        const idx = prevData.findIndex(item => item && item.id === msg.id);
        if (idx === -1) return prevData;
        const updated = [...prevData];
        updated[idx] = {
          ...updated[idx],
          [msg.field]: msg.value,
          lead_contacted: msg.value,
          lead_last_email: {
            ...updated[idx].lead_last_email,
            timestamp: msg.timestamp,
            status: msg.value
          },
          updated_at: msg.timestamp,
          last_contact_date: new Date(msg.timestamp).toISOString(),
          _local_status_update: true,
          _optimistic_update: true,
          _client_update_timestamp: msg.timestamp,
          ...(isCritical ? { _critical_update: true, _prevent_refresh: true } : {})
        };
        return updated;
      };
      setFullDbData(updater);
      setFilteredData(updater);
      setDisplayedData(updater);
    };

    if (status === 'status-update' && message) {
      patchContact(message);
      return;
    } else if (status === 'summary') {
      // Spezielle Behandlung für Zusammenfassungen (Bulk-Mode etc.)
      setEmailNotifier({ 
        status: 'summary', 
        message, 
        scheduledTime: null,
        recipientEmail: null,
        senderEmail: null
      });
    } else if (status === 'flow-update' || status === 'tag-update' || status === 'contact-deleted') {
      // Internal events — update data silently, no toast
      if (status === 'flow-update' && message && typeof message === 'object') {
        setFullDbData(prevData => {
          const idx = prevData.findIndex(item => item && item.id === message.id);
          if (idx !== -1) {
            const updated = [...prevData];
            updated[idx] = { ...updated[idx], warmupFlow: message.warmupFlow };
            if (message.flowEndStatus) updated[idx].lead_contacted = message.flowEndStatus;
            return updated;
          }
          return prevData;
        });
      }
      if (status === 'contact-deleted' && message) {
        const deletedId = typeof message === 'object' ? message.id : message;
        setFullDbData(prev => prev.filter(item => item && item.id !== deletedId));
        setFilteredData(prev => prev.filter(item => item && item.id !== deletedId));
        setDisplayedData(prev => prev.filter(item => item && item.id !== deletedId));
      }
    } else {
      // Normale Benachrichtigungen wie bisher (nur für E-Mail-Versand)
      const safeMessage = (typeof message === 'object' && message !== null) 
        ? (message.message || JSON.stringify(message)) 
        : message;
      setEmailNotifier({ 
        status, 
        message: safeMessage, 
        scheduledTime: validScheduledTime,
        recipientEmail: recipientEmail || (rowData ? rowData.lead_email : null),
        senderEmail: senderEmail || null
      });
    }
  };

  // Die Import-Namen werden von NewWorldDbTable selbst geladen (dort speisen sie
  // den Import-Filter). Der frühere Loader an dieser Stelle war eine Dublette:
  // sein Ergebnis landete in `importOptions`, das nie gelesen wurde, weil
  // WorldDbImportFilter hier nicht gerendert wird. Er kostete pro Import je eine
  // Count-Query UND einen Write auf import_names — bei Accounts mit vielen
  // Imports also dutzende Roundtrips pro Öffnen der WorldDb, für nichts.

  // Neue Funktion, die manuell ausgelöst wird, um die angezeigten Daten zu aktualisieren
  const updateDisplayedData = () => {
    // Die Filterung erfolgt jetzt über Firestore, daher einfach die aktuellen Daten übernehmen
    setFilteredData(fullDbData);
    setDisplayedData(fullDbData.slice(0, displayLimit));
    setDbData(fullDbData.slice(0, displayLimit));
    setHasMoreData(fullDbData.length > displayLimit);
  };

  useEffect(() => {
    setCountReady(false);
    fetchTotalContactCount();
  }, [fetchTotalContactCount]);

  // Warte bis TeamContext fertig geladen hat
  if (teamLoading) {
    return <LoadingSpinner />;
  }

  if (!userId) {
    return <p className="errorMessage">Please login to view your data.</p>;
  }

  return (
    <>
      <WorldEmptyBackdrop theme={emptyTheme} />

      {needsMigration && (
        <WorldDbMigrationModal 
          userId={userId}
          onMigrationComplete={handleMigrationComplete}
        />
      )}

      <DataConnectorAlertModal
        userId={userId}
        onOpenSettings={() => {
          document.dispatchEvent(new CustomEvent('openSettingsTab', { detail: { tab: DATA_CONNECTOR_SETTINGS_TAB } }));
          navigate(`/world?view=settings&tab=${DATA_CONNECTOR_SETTINGS_TAB}`);
        }}
      />

      {isSettingsView && <WorldSettings />}
      {isOfferView && <OfferCreator offerIdFromUrl={offerId} onNavigate={(path) => navigate(path)} />}
      {isPayoutsView && <ComingSoon />}

      {keepTargets && (
        <div hidden={!isTargetsView || needsMigration} className="world-keep-pane">
          {!countReady ? (
            <LoadingSpinner />
          ) : totalContactCount === 0 ? (
            <WorldDbEmptyStart
              canWrite={canWrite()}
              onImportList={() => setShowUploader(true)}
              onAddContact={() => setShowCreator(true)}
              onConnectApi={() => navigate('/world?view=settings&tab=api')}
            />
          ) : (
            <div>
              <ApiPendingBanner onReviewClick={() => setShowApiReview(true)} />
              <NewWorldDbTable 
                key={userId}
                userId={userId}
                setShowUploader={setShowUploader}
                setShowCreator={setShowCreator}
                totalDatabaseCount={totalContactCount}
                contactedToday={todayContactedCount}
                onEmailResult={handleEmailResult}
                onReload={reloadData}
                reloadFlag={reloadFlag}
              />
            </div>
          )}
        </div>
      )}

      {keepInbox && (
        <div hidden={!isInboxView || needsMigration} className="world-keep-pane">
          {useInboxV2 ? (
            <InboxView
              userId={userId}
              onNavigateToSettings={openInboxReplySettings}
              onEmptyChange={setInboxEmpty}
            />
          ) : (
            <SmartInbox userId={userId} onNavigateToSettings={() => setActiveView('settings')} />
          )}
        </div>
      )}

      {keepLeads && (
        <div hidden={!isLeadsView || needsMigration} className="world-keep-pane">
          <Leads onEmptyChange={setLeadsEmpty} />
        </div>
      )}

      {showUploader && <WorldDbUploader onClose={() => setShowUploader(false)} onReload={reloadData} userId={userId} />}
      
      {showApiReview && (
        <ApiStagingReview 
          onClose={() => setShowApiReview(false)} 
          onImportComplete={() => {
            setShowApiReview(false);
            reloadData();
          }}
        />
      )}
      
      {showCreator && <WorldDbCreator 
        onClose={() => setShowCreator(false)} 
        userId={userId}
        onCreated={() => {
          setShowCreator(false);
          reloadData();
        }}
      />}

      {/* E-Mail-Benachrichtigung am unteren Bildschirmrand */}
      <EmailSentNotifier
        status={emailNotifier.status}
        message={emailNotifier.message}
        onClose={resetEmailNotifier}
        scheduledTime={emailNotifier.scheduledTime}
        recipientEmail={emailNotifier.recipientEmail}
        senderEmail={emailNotifier.senderEmail}
      />
    </>
  );
};

export default WorldDb;
